import * as XLSX from 'xlsx';

export interface ParsedMaterialInput {
  code: string;
  name: string;
  category: string;
  supplier: string;
  family_code?: string | null;
  ean_code?: string | null;
  description?: string;

  thickness?: number;
  is_variable_thickness?: boolean;
  width?: number;
  length?: number;
  weight_per_sqm?: number;
  weight_per_ml?: number;
  sheet_thickness?: number;
  density?: number;

  list_price?: number;
  unit_price: number;
  unit: string;
  discount?: number;
  extra_discount?: number;
  delivery_indicator?: string;
  valid_until?: string;

  incidence_base?: number;
  incidence_per_sqm?: number;
  passo?: number;
  installation_time_per_sqm?: number;
  box_pieces?: number;
  /**
   * F21.4 — Sfrido %: number = override esplicito, null = "usa default categoria"
   * (forzato dalla colonna 'Sfrido tipo' = 'Default categoria'), undefined = no info.
   */
  waste_percentage?: number | null;
  disposal_percentage?: number;
  installation_notes?: string;

  material_type?: string;
  board_typology?: string;
  board_type?: string;
  flexural_strength?: string;
  surface_hardness?: string;
  en_520_type?: string;
  water_absorption?: string;
  humidity_resistance_class?: string;
  thermal_conductivity?: number;
  acoustic_performance?: number;

  fire_resistance_class?: string;
  fire_class?: string;
  fire_description?: string;
  fire_usage_notes?: string;
  fire_performance_notes?: string;
  rei_compatible?: boolean;

  profile_type?: string;
  surface_finish?: string;

  environmental_certification?: string;
  recycled_content?: number;
  voc_class?: string;
  sustainability_notes?: string;
  carbon_footprint?: string;
  epd?: string;
  vapor_permeability?: string;
  thermal_capacity?: string;

  mechanical_performance?: string;
  thermal_performance_notes?: string;
  system_compatibility?: string;

  color_hex?: string;
  intended_use?: string[];
  compatible_board_types?: string[];
}

function parseYesNo(val: any): boolean | undefined {
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();
    if (v === "sì" || v === "si" || v === "true" || v === "1") return true;
    if (v === "no" || v === "false" || v === "0") return false;
  }
  if (typeof val === "boolean") return val;
  return undefined;
}
function parseArray(val: any): string[] | undefined {
  if (val === null || val === undefined || val === "") return undefined;
  if (typeof val === "string") return val.split(",").map((s) => s.trim()).filter(Boolean);
  if (Array.isArray(val)) return val;
  return undefined;
}
function numOrUndef(val: any): number | undefined {
  if (val === null || val === undefined || val === "") return undefined;
  const n = Number(val);
  return Number.isFinite(n) ? n : undefined;
}
function strOrUndef(val: any): string | undefined {
  if (val === null || val === undefined) return undefined;
  const s = String(val).trim();
  return s === "" ? undefined : s;
}

export async function parseMaterialsExcel(file: File): Promise<{ materials: ParsedMaterialInput[]; errors: string[] }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Cerca il foglio "Materiali completi" oppure il primo che NON è "Leggimi"
        const sheetName =
          workbook.SheetNames.find(n => /materiali/i.test(n)) ||
          workbook.SheetNames.find(n => n.toLowerCase() !== 'leggimi') ||
          workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const errors: string[] = [];
        const materials: ParsedMaterialInput[] = [];

        json.forEach((row, i) => {
          // Validazione minimal — gli unici veramente obbligatori sono code, name, category, supplier, unit_price, unit
          const missing: string[] = [];
          if (!row.Codice) missing.push("Codice");
          if (!row.Nome) missing.push("Nome");
          if (!row.Categoria) missing.push("Categoria");
          if (!row.Fornitore) missing.push("Fornitore");
          if (row["Prezzo unitario (€)"] === "" || row["Prezzo unitario (€)"] === undefined || row["Prezzo unitario (€)"] === null) missing.push("Prezzo unitario");
          if (!row["Unità"]) missing.push("Unità");
          if (missing.length) {
            errors.push(`Riga ${i + 2}: campi obbligatori mancanti (${missing.join(", ")}).`);
            return;
          }

          let discount = numOrUndef(row["Sconto (%)"]) ?? 0;
          if (discount < 0 || discount > 100) {
            errors.push(`Riga ${i + 2}: Sconto fuori range (0-100%), riportato a 0.`);
            discount = 0;
          }

          materials.push({
            code: String(row.Codice).trim(),
            name: String(row.Nome).trim(),
            category: String(row.Categoria).trim(),
            supplier: String(row.Fornitore).trim(),
            family_code: strOrUndef(row["Codice famiglia"]) ?? null,
            ean_code: strOrUndef(row["EAN"]) ?? null,
            description: strOrUndef(row.Descrizione),

            thickness: numOrUndef(row["Spessore (mm)"]),
            is_variable_thickness: parseYesNo(row["Spessore variabile"]),
            width: numOrUndef(row["Larghezza (mm)"]),
            length: numOrUndef(row["Lunghezza (mm)"]),
            weight_per_sqm: numOrUndef(row["Peso (kg/m²)"]),
            weight_per_ml: numOrUndef(row["Peso per ml (kg)"]),
            sheet_thickness: numOrUndef(row["Spessore lamiera (mm)"]),
            density: numOrUndef(row["Densità (kg/m³)"]),

            list_price: numOrUndef(row["Prezzo listino (€)"]),
            unit_price: Number(row["Prezzo unitario (€)"]),
            unit: String(row["Unità"]).trim(),
            discount,
            extra_discount: numOrUndef(row["Extra sconto (%)"]),
            delivery_indicator: strOrUndef(row["Indicatore consegna"]),
            valid_until: strOrUndef(row["Valido fino al"]),

            incidence_base: numOrUndef(row["Incidenza Base"]),
            incidence_per_sqm: numOrUndef(row["Incidenza (unit/m²)"]),
            passo: numOrUndef(row["Passo (mm)"]),
            installation_time_per_sqm: numOrUndef(row["Tempo installazione (ore/m²)"]),
            box_pieces: numOrUndef(row["Pezzi per scatola"]),
            // F21.4 — Sfrido: rispetta colonna "Sfrido tipo" se presente.
            //   - 'Default categoria' (case insensitive, anche 'default' o 'def.'): forza NULL (= usa default da Settings)
            //   - 'Override' (case insensitive): applica il valore numerico come override
            //   - colonna assente (file vecchi): back-compat. Cella vuota = NULL, numero = override.
            // null sentinel: applicato come "imposta a NULL" lato handleSaveImport.
            waste_percentage: ((): number | null | undefined => {
              const typeRaw = String(row["Sfrido tipo"] ?? '').trim().toLowerCase();
              const num = numOrUndef(row["Sfrido %"]);
              if (typeRaw.startsWith('default') || typeRaw.startsWith('def')) return null;
              if (typeRaw === 'override') return num;
              // Back-compat: nessuna colonna 'Sfrido tipo'
              return num;
            })(),
            disposal_percentage: numOrUndef(row["Smaltimento %"]),
            installation_notes: strOrUndef(row["Note installazione"]),

            material_type: strOrUndef(row["Tipo materiale"]),
            board_typology: strOrUndef(row["Tipologia lastra"]),
            board_type: strOrUndef(row["Tipo lastra"]),
            flexural_strength: strOrUndef(row["Resistenza a flessione"]),
            surface_hardness: strOrUndef(row["Durezza superficiale"]),
            en_520_type: strOrUndef(row["EN 520"]),
            water_absorption: strOrUndef(row["Assorbimento acqua"]),
            humidity_resistance_class: strOrUndef(row["Classe resistenza umidità"]),
            thermal_conductivity: numOrUndef(row["Conduttività termica (λ)"]),
            acoustic_performance: numOrUndef(row["Prestazione acustica (Rw)"]),

            fire_resistance_class: strOrUndef(row["Resistenza fuoco - classe"]),
            fire_class: strOrUndef(row["Classe fuoco"]),
            fire_description: strOrUndef(row["Descrizione fuoco"]),
            fire_usage_notes: strOrUndef(row["Note utilizzo fuoco"]),
            fire_performance_notes: strOrUndef(row["Note prestazioni fuoco"]),
            rei_compatible: parseYesNo(row["Compatibile REI"]),

            profile_type: strOrUndef(row["Tipo profilo"]),
            surface_finish: strOrUndef(row["Finitura superficiale"]),

            environmental_certification: strOrUndef(row["Certificazione ambientale"]),
            recycled_content: numOrUndef(row["Contenuto riciclato (%)"]),
            voc_class: strOrUndef(row["Classe VOC"]),
            sustainability_notes: strOrUndef(row["Note sostenibilità"]),
            carbon_footprint: strOrUndef(row["Carbon Footprint"]),
            epd: strOrUndef(row["EPD"]),
            vapor_permeability: strOrUndef(row["Permeabilità vapore"]),
            thermal_capacity: strOrUndef(row["Capacità termica"]),

            mechanical_performance: strOrUndef(row["Prestazioni meccaniche"]),
            thermal_performance_notes: strOrUndef(row["Note termo-igrometriche"]),
            system_compatibility: strOrUndef(row["Compatibilità sistema"]),

            color_hex: strOrUndef(row["Colore (hex)"]),
            intended_use: parseArray(row["Destinazioni d'uso"]),
            compatible_board_types: parseArray(row["Lastre compatibili"]),
          });
        });

        resolve({ materials, errors });
      } catch (err) {
        resolve({ materials: [], errors: [`Formato file non valido o errore durante la lettura del file: ${(err as Error).message}`] });
      }
    };
    reader.readAsArrayBuffer(file);
  });
}
